import { cn } from "@/lib/cn";
import { DURATION, TRANSITION } from "@/lib/motion";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

/**
 * Seconds a height change takes. A tween, not a spring: a spring overshoots and every sibling below
 * rides the overshoot, so the column wobbles; a monotonic tween only ever settles downward.
 */
export const COLLAPSE_DURATION = TRANSITION.size.duration;

export type CollapseProps = {
  open: boolean;
  children: ReactNode;
  /**
   * Seconds to wait before expanding. Use `COLLAPSE_DURATION` when another panel in the same column
   * collapses on the same change: two concurrent height animations make the content between them drift.
   */
  enterDelay?: number;
  /** Applied to the animated wrapper. Keep margins on the child: a margin here survives height 0. */
  className?: string;
};

/** Reveals its content by animating height and opacity instead of snapping it into the layout. */
export function Collapse({ open, children, className, enterDelay = 0 }: CollapseProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return open ? <div className={cn("overflow-hidden", className)}>{children}</div> : null;
  }

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="collapse"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            height: { ...TRANSITION.size, delay: enterDelay },
            opacity: { duration: DURATION.fast, delay: enterDelay },
          }}
          className={cn("overflow-hidden", className)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
